/**
 * Payroll Completion Service — Approve / Reject transactional actions.
 *
 * See PRD_PayrollApproval.md §Side Effect Migration for the rationale behind
 * the two-phase Submit / Approve split. The deduction write-back was lifted
 * out of ProcessPayroll's handleCompleteConfirm and lives here so that:
 *   - Submit performs no financial side effects (safe to reject).
 *   - Approve performs the write-back exactly once per approved cycle.
 *
 * Both actions use a Firestore transaction that asserts
 * status === 'pending-approval' before writing, preventing double-approve
 * and approve-after-reject races (PRD §Concurrent-Action Race Safety).
 */

import {
  collection,
  doc,
  setDoc,
  writeBatch,
  increment,
  arrayUnion,
  runTransaction,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { payrollIndexRef, mergeIndexData } from './payrollIndexService';
import { incrementPendingApproval } from './payrollApprovalSummaryService';
import { fetchCompanyLoans } from './loanService';
import { toYMDString } from '../utils/periodDate';
import { isCreditable, num } from '../utils/loanMath';
import { round2 } from './contributionTableClient';

// Mapping from payroll deduction keys to DTR document field names.
// Source of truth — also referenced in ProcessPayroll.js for read-time
// totals, but only this module performs the write-back.
export const DEDUCTION_TO_DTR_FIELD = {
  sss: 'sssDeducted',
  philhealth: 'philhealthDeducted',
  pagibig: 'pagibigDeducted',
  tax: 'withholdingTaxDeducted',
};

/**
 * Apply the deduction write-back to DTR documents.
 * Extracted verbatim from ProcessPayroll.js handleCompleteConfirm.
 *
 * Failures are non-fatal: logs to companies/{companyId}/issues collection
 * with type: 'dtr_writeback_failure' for later reconciliation.
 *
 * @param {string} companyId
 * @param {Object} run - completed payroll run with employees + deductions
 * @param {string} triggeringUserId
 * @returns {Promise<{ success: boolean, failureCount: number, affectedEmployees: string[] }>}
 */
// Chunked deduction write-back: one sequentially-awaited writeBatch commit at a
// time stays well under the SDK's queued-write cap (C2 — the Approve-path fix).
// Mirrors payrollRunStore's op cap.
const MAX_DEDUCTION_BATCH_OPS = 450;

export const applyDeductionWriteBack = async (companyId, run, triggeringUserId) => {
  const { runId, periodStart, periodEnd, employees, payrollCycle } = run;

  try {
    // Collect blind increment() ops — server-atomic, no prior read, so batchable.
    const dtrUpdates = [];

    for (const emp of employees) {
      const employeeId = emp.employeeId || emp.id;
      const updatesByMonth = {};

      for (const [deductionKey, dtrField] of Object.entries(DEDUCTION_TO_DTR_FIELD)) {
        const deductionByMonth = emp.deductions?.[deductionKey];
        if (typeof deductionByMonth === 'object' && deductionByMonth !== null && !Array.isArray(deductionByMonth)) {
          for (const [monthKey, amount] of Object.entries(deductionByMonth)) {
            if (amount && amount > 0) {
              if (!updatesByMonth[monthKey]) {
                updatesByMonth[monthKey] = {};
              }
              updatesByMonth[monthKey][dtrField] = increment(amount);
            }
          }
        }
      }

      for (const [monthKey, fields] of Object.entries(updatesByMonth)) {
        dtrUpdates.push({
          employeeId,
          monthKey,
          ref: doc(db, 'companies', companyId, 'dailyTimeRecords', `${monthKey}_${employeeId}`),
          // Stamp the two keys the ledger READ needs — readDtrByMonth queries
          // where('month','in',…) and groupDtrByEmployee drops a doc with no
          // employeeId — so a doc this set-merge CREATES (an employee with no time
          // records that month) is visible to the next run. Without them the
          // balance chip read the ledger as 0 and offered the full contribution
          // again (PRD_SssContributionBasis /impl-review r1). Same values
          // saveTimeRecord writes; a no-op on a doc that already has them.
          fields: { ...fields, month: monthKey, employeeId },
        });
      }
    }

    if (dtrUpdates.length === 0) {
      return { success: true, failureCount: 0, affectedEmployees: [] };
    }

    // Commit in chunked, sequentially-awaited writeBatches (C2): one bounded commit
    // at a time can't exhaust the queued-write cap that the old per-employee
    // Promise.allSettled fan-out hit. Use set(..., {merge:true}) NOT update() so a
    // missing DTR doc (e.g. a deduction scheduled for a month the employee has no
    // records) doesn't reject the whole atomic chunk and drop every other employee's
    // increment — set-merge creates the doc and increment() starts from 0 (impl-review F1).
    // A chunk commit can still fail on a real error; granularity is then per-CHUNK.
    const failures = [];
    for (let i = 0; i < dtrUpdates.length; i += MAX_DEDUCTION_BATCH_OPS) {
      const chunk = dtrUpdates.slice(i, i + MAX_DEDUCTION_BATCH_OPS);
      const batch = writeBatch(db);
      chunk.forEach((u) => batch.set(u.ref, u.fields, { merge: true }));
      try {
        await batch.commit();
      } catch (chunkErr) {
        console.error('[payrollCompletionService] Deduction write-back chunk failed:', chunkErr);
        chunk.forEach((u) => failures.push({
          employeeId: u.employeeId,
          monthKey: u.monthKey,
          error: chunkErr?.message || 'Unknown error',
        }));
      }
    }

    if (failures.length === 0) {
      return { success: true, failureCount: 0, affectedEmployees: [] };
    }

    const affectedEmployees = [...new Set(failures.map((f) => f.employeeId))];
    console.error('[payrollCompletionService] Some DTR deduction batches failed:', failures);

    try {
      const issueRef = doc(collection(db, 'companies', companyId, 'issues'));
      await setDoc(issueRef, {
        type: 'dtr_writeback_failure',
        severity: 'warning',
        source: 'payroll_approval',
        summary: `Deduction records for ${affectedEmployees.length} employee(s) failed to update`,
        context: {
          payrollRunId: runId,
          cycleId: payrollCycle?.cycleId || '',
          periodStart,
          periodEnd,
        },
        details: {
          totalAttempted: dtrUpdates.length,
          totalFailed: failures.length,
          affectedEmployees,
          failures,
        },
        status: 'open',
        resolvedBy: null,
        resolvedAt: null,
        resolution: null,
        createdAt: serverTimestamp(),
        triggeredBy: triggeringUserId,
      });
    } catch (logErr) {
      console.error('[payrollCompletionService] Failed to log issue:', logErr);
    }

    return { success: false, failureCount: failures.length, affectedEmployees };
  } catch (dtrErr) {
    console.error('[payrollCompletionService] Error updating DTR deduction records:', dtrErr);

    try {
      const issueRef = doc(collection(db, 'companies', companyId, 'issues'));
      await setDoc(issueRef, {
        type: 'dtr_writeback_failure',
        severity: 'error',
        source: 'payroll_approval',
        summary: 'DTR deduction write-back failed entirely',
        context: {
          payrollRunId: runId,
          cycleId: payrollCycle?.cycleId || '',
          periodStart,
          periodEnd,
        },
        details: {
          error: dtrErr.message || 'Unknown error',
        },
        status: 'open',
        resolvedBy: null,
        resolvedAt: null,
        resolution: null,
        createdAt: serverTimestamp(),
        triggeredBy: triggeringUserId,
      });
    } catch (logErr) {
      console.error('[payrollCompletionService] Failed to log issue:', logErr);
    }

    return { success: false, failureCount: -1, affectedEmployees: [] };
  }
};

// Same per-commit backstop as the DTR write-back above.
const MAX_LOAN_BATCH_OPS = 450;

const logIssue = async (companyId, payload) => {
  try {
    await setDoc(doc(collection(db, 'companies', companyId, 'issues')), {
      status: 'open',
      resolvedBy: null,
      resolvedAt: null,
      resolution: null,
      createdAt: serverTimestamp(),
      ...payload,
    });
  } catch (logErr) {
    console.error('[payrollCompletionService] Failed to log issue:', logErr);
  }
};

/**
 * PRD_LoansManagement C5/C13 — credit each loan's ledger for what this run
 * deducted. The DTR write-back's twin: blind increment() ops (plus an
 * arrayUnion() history entry naming the approver), chunked, non-fatal, logged
 * to `issues` on failure. Differences from the DTR path:
 *   - it re-reads the loans first (ONE getDocs — the C13 query) and SKIPS an
 *     entry whose loan is voided (a data-entry mistake that should never have
 *     been deducted); a paused or closed loan is still credited, because the
 *     money was genuinely taken from the employee's pay;
 *   - a skipped entry is logged as `loan_writeback_skipped` — nothing in the
 *     app reads `issues` (the rules admit create by payroll:approve and nothing
 *     else), so the approver learns of it from the console; the
 *     refund is an Adjustments-step earning adjustment next run (C13);
 *   - it uses update(), not set-merge: a loan doc that does not exist must
 *     fail the chunk, never be created;
 *   - one update per LOAN (a cross-month run carries two entries for one loan,
 *     merged into one op: two dotted increments and a two-element arrayUnion).
 * Period bounds go through toYMDString — a historical run hands them over as
 * Timestamps (the date-boundary rule); paidAt is an ISO instant, not a date.
 *
 * @param {string} companyId
 * @param {Object} run - { runId, periodStart, periodEnd, employees, payrollCycle }
 * @param {string} approverUserId
 * @returns {Promise<{ success: boolean, failureCount: number, skipped: Array, affectedLoans: string[] }>}
 */
export const applyLoanWriteBack = async (companyId, run, approverUserId) => {
  const { runId, periodStart, periodEnd, employees, payrollCycle } = run;
  const startYMD = toYMDString(periodStart);
  const endYMD = toYMDString(periodEnd);
  const paidAt = new Date().toISOString();
  const context = { payrollRunId: runId, cycleId: payrollCycle?.cycleId || '', periodStart: startYMD, periodEnd: endYMD };

  try {
    // Entries worth crediting: a positive amount against a named loan.
    const entries = [];
    for (const emp of employees || []) {
      const employeeId = emp.employeeId || emp.id;
      const loans = Array.isArray(emp.deductions?.loans) ? emp.deductions.loans : [];
      for (const e of loans) {
        const amount = round2(num(e?.amount));
        if (e?.loanId && amount > 0) entries.push({ employeeId, loanId: e.loanId, name: e.name || null, monthKey: e.monthKey, amount });
      }
    }
    if (entries.length === 0) {
      return { success: true, failureCount: 0, skipped: [], affectedLoans: [] };
    }

    // One query for the whole run (C13); a referenced loan absent from the result is voided.
    const live = new Map((await fetchCompanyLoans(companyId)).map((l) => [l.id, l]));

    const skipped = [];
    const byLoan = new Map(); // loanId → the merged update
    for (const entry of entries) {
      const loan = live.get(entry.loanId);
      if (!loan || !isCreditable(loan)) {
        skipped.push(entry);
        continue;
      }
      if (!byLoan.has(entry.loanId)) {
        byLoan.set(entry.loanId, { loanId: entry.loanId, employeeId: entry.employeeId, total: 0, byMonth: {}, history: [] });
      }
      const op = byLoan.get(entry.loanId);
      op.total = round2(op.total + entry.amount);
      op.byMonth[entry.monthKey] = round2((op.byMonth[entry.monthKey] || 0) + entry.amount);
      op.history.push({
        runId,
        periodStart: startYMD,
        periodEnd: endYMD,
        monthKey: entry.monthKey,
        amount: entry.amount,
        paidAt,
        by: approverUserId,
      });
    }

    if (skipped.length > 0) {
      console.error('[payrollCompletionService] Loan write-back skipped voided loans:', skipped);
      await logIssue(companyId, {
        type: 'loan_writeback_skipped',
        severity: 'warning',
        source: 'payroll_approval',
        summary: `${skipped.length} loan installment(s) were deducted from pay but not credited — the loan was voided`,
        context,
        details: { skipped },
        triggeredBy: approverUserId,
      });
    }

    const ops = [...byLoan.values()].map((op) => ({
      loanId: op.loanId,
      employeeId: op.employeeId,
      ref: doc(db, 'companies', companyId, 'loans', op.loanId),
      fields: {
        paidTotal: increment(op.total),
        ...Object.fromEntries(Object.entries(op.byMonth).map(([k, v]) => [`paidByMonth.${k}`, increment(v)])),
        payments: arrayUnion(...op.history),
        updatedAt: serverTimestamp(),
      },
    }));

    const failures = [];
    for (let i = 0; i < ops.length; i += MAX_LOAN_BATCH_OPS) {
      const chunk = ops.slice(i, i + MAX_LOAN_BATCH_OPS);
      const batch = writeBatch(db);
      chunk.forEach((u) => batch.update(u.ref, u.fields));
      try {
        await batch.commit();
      } catch (chunkErr) {
        console.error('[payrollCompletionService] Loan write-back chunk failed:', chunkErr);
        chunk.forEach((u) => failures.push({ loanId: u.loanId, employeeId: u.employeeId, error: chunkErr?.message || 'Unknown error' }));
      }
    }

    if (failures.length === 0) {
      return { success: true, failureCount: 0, skipped, affectedLoans: [] };
    }

    const affectedLoans = [...new Set(failures.map((f) => f.loanId))];
    console.error('[payrollCompletionService] Some loan ledger batches failed:', failures);
    await logIssue(companyId, {
      type: 'loan_writeback_failure',
      severity: 'warning',
      source: 'payroll_approval',
      summary: `Loan ledgers for ${affectedLoans.length} loan(s) failed to update`,
      context,
      details: { totalAttempted: ops.length, totalFailed: failures.length, affectedLoans, failures },
      triggeredBy: approverUserId,
    });
    return { success: false, failureCount: failures.length, skipped, affectedLoans };
  } catch (err) {
    console.error('[payrollCompletionService] Error updating loan ledgers:', err);
    await logIssue(companyId, {
      type: 'loan_writeback_failure',
      severity: 'error',
      source: 'payroll_approval',
      summary: 'Loan ledger write-back failed entirely',
      context,
      details: { error: err?.message || 'Unknown error' },
      triggeredBy: approverUserId,
    });
    return { success: false, failureCount: -1, skipped: [], affectedLoans: [] };
  }
};

/**
 * Approve a pending payroll run.
 *
 * Transactional: asserts current status is 'pending-approval' before
 * promoting to 'completed'. After the transaction:
 *   1. Promote DTR locks from pending-approval to completed.
 *   2. Run the deduction write-back (non-fatal on failure), then the loan
 *      ledger write-back (PRD_LoansManagement C5 — same posture).
 *   3. Update payroll index entry status.
 *   4. Decrement summaries/payroll.pendingApproval.
 *
 * @param {Object} args
 * @param {string} args.companyId
 * @param {Object} args.run - The payroll run document (must include runId, status, periodStart, periodEnd, employees, etc.)
 * @param {string} args.approverUserId
 * @returns {Promise<{ completedRun: Object, lockResult: Object, writeBackResult: Object, loanWriteBackResult: Object }>}
 * @throws Error if status is not 'pending-approval', or if maker-checker policy is
 *   in force and approverUserId === submittedBy (the policy is re-read inside the
 *   transaction so a stale-tab caller cannot bypass the UI check).
 */
export const approvePayroll = async ({ companyId, run, approverUserId }) => {
  const runRef = doc(db, 'companies', companyId, 'payrollRuns', run.runId);
  const settingsRef = doc(db, 'companies', companyId, 'settings', 'payrollRates');
  const masterRef = payrollIndexRef(companyId);
  const nowJs = new Date();

  // Transactional status flip + approvalSnapshot + audit append + completedBy.
  // Asserts current status is 'pending-approval' to block double-approve races,
  // AND re-reads the maker-checker policy from Firestore (UI-only checks are
  // bypassable via a stale tab or a direct service call). See PRD §35 and
  // Post-Implementation Checklist §E.
  const completedRun = await runTransaction(db, async (tx) => {
    const [snap, settingsSnap, masterSnap] = await Promise.all([
      tx.get(runRef),
      tx.get(settingsRef),
      tx.get(masterRef),
    ]);
    if (!snap.exists()) {
      throw new Error('Payroll run not found.');
    }
    const current = snap.data();
    if (current.status !== 'pending-approval') {
      throw new Error('This run has already been actioned.');
    }

    // Server-authoritative policy — never trust the caller for this.
    const policy = settingsSnap.exists()
      ? (settingsSnap.data().payrollApprovalType || 'self-approval')
      : 'self-approval';
    const submittedBy = current.submittedBy || null;
    const wasSelfApproval = submittedBy === approverUserId;

    if (policy === 'maker-checker' && wasSelfApproval) {
      throw new Error('Maker-checker is enabled. You cannot approve your own submission.');
    }

    const existingTrail = Array.isArray(current.auditTrail) ? current.auditTrail : [];
    const newTrailEntry = {
      action: 'approved',
      by: approverUserId,
      at: nowJs,
      reason: null,
    };

    const updates = {
      status: 'completed',
      approvalSnapshot: {
        approvalType: policy,
        approvedBy: approverUserId,
        submittedBy,
        approvedAt: nowJs,
        wasSelfApproval,
      },
      auditTrail: [...existingTrail, newTrailEntry],
      completedBy: approverUserId,
      lastModified: serverTimestamp(),
      lastModifiedBy: approverUserId,
    };

    tx.update(runRef, updates);

    // C9 — fold the index write into the same transaction so the lock-bearing
    // index can't drift from the completed status (atomic + retry-safe).
    tx.set(masterRef, mergeIndexData(masterSnap.exists() ? masterSnap.data() : null, run.runId, {
      runId: run.runId,
      periodStart: toYMDString(run.periodStart),
      periodEnd: toYMDString(run.periodEnd),
      status: 'completed',
      // Prefer the parent-resident selectedEmployeeIds (a converted run's parent has
      // no inline employees); fall back to hydrated employees (impl-review F4 —
      // symmetry with reject; avoids an empty employeeIds → completed run deriving unlocked).
      employeeIds: run.selectedEmployeeIds || (run.employees || []).map((e) => e.employeeId || e.id),
    }));

    // Hand back a caller-safe shape: real timestamps in place of serverTimestamp
    // sentinels so result.completedRun.<ts>.toDate() works downstream.
    return {
      ...current,
      ...updates,
      lastModified: nowJs,
    };
  });

  // Post-transaction side effects. Failures here are logged but do not
  // roll back the status flip — see PRD §Inherited risk.
  // DTR locks are derived from the index (C1): the in-tx index write above set this
  // run 'completed', so there's no per-employee lock promotion. lockResult is kept
  // as a success no-op for the unchanged return shape.
  const lockResult = { success: true, errors: [], failedEmployees: [] };

  const writeBackRun = {
    runId: run.runId,
    periodStart: run.periodStart,
    periodEnd: run.periodEnd,
    employees: run.employees || [],
    payrollCycle: run.payrollCycle,
  };
  const writeBackResult = await applyDeductionWriteBack(companyId, writeBackRun, approverUserId);
  // PRD_LoansManagement C5: the loan ledger, right after the DTR ledger, same posture.
  const loanWriteBackResult = await applyLoanWriteBack(companyId, writeBackRun, approverUserId);

  // Summary counter decrement — non-fatal. (Index entry is now written atomically
  // inside the transaction above — C9 — so there's no post-tx index update here.)
  try {
    await incrementPendingApproval(companyId, -1);
  } catch (summaryErr) {
    console.error('[payrollCompletionService] Failed to decrement pending counter:', summaryErr);
  }

  return { completedRun, lockResult, writeBackResult, loanWriteBackResult };
};

/**
 * Reject a pending payroll run.
 *
 * Transactional: asserts current status is 'pending-approval' before
 * reverting to 'draft'. After the transaction:
 *   1. Release pending-approval DTR locks.
 *   2. Update payroll index entry status back to 'draft'.
 *   3. Decrement summaries/payroll.pendingApproval.
 *
 * @param {Object} args
 * @param {string} args.companyId
 * @param {Object} args.run - The payroll run document
 * @param {string} args.rejecterUserId
 * @param {string} args.reason - Non-empty rejection reason
 * @returns {Promise<{ updatedRun: Object, lockResult: Object }>}
 * @throws Error if status is not 'pending-approval', or reason is empty
 */
export const rejectPayroll = async ({ companyId, run, rejecterUserId, reason }) => {
  const trimmedReason = (reason || '').trim();
  if (!trimmedReason) {
    throw new Error('A rejection reason is required.');
  }

  const runRef = doc(db, 'companies', companyId, 'payrollRuns', run.runId);
  const masterRef = payrollIndexRef(companyId);
  // Reject runs (post-C6) carry no employees on the parent — derive IDs once from
  // the parent-resident selectedEmployeeIds (inline fallback for historical runs);
  // used by the in-tx index write (C9) AND the post-tx lock release (C6).
  const employeeIds = run.selectedEmployeeIds || (run.employees || []).map((e) => e.employeeId || e.id);
  const nowJs = new Date();

  const updatedRun = await runTransaction(db, async (tx) => {
    const [snap, masterSnap] = await Promise.all([tx.get(runRef), tx.get(masterRef)]);
    if (!snap.exists()) {
      throw new Error('Payroll run not found.');
    }
    const current = snap.data();
    if (current.status !== 'pending-approval') {
      throw new Error('This run has already been actioned.');
    }

    const existingTrail = Array.isArray(current.auditTrail) ? current.auditTrail : [];
    const newTrailEntry = {
      action: 'rejected',
      by: rejecterUserId,
      at: nowJs,
      reason: trimmedReason,
    };

    const updates = {
      status: 'draft',
      lastRejection: {
        reason: trimmedReason,
        by: rejecterUserId,
        at: nowJs,
      },
      auditTrail: [...existingTrail, newTrailEntry],
      // Clear submission metadata — next Submit will set fresh values
      submittedBy: null,
      submittedAt: null,
      lastModified: serverTimestamp(),
      lastModifiedBy: rejecterUserId,
    };

    tx.update(runRef, updates);

    // C9 — fold the index write into the same transaction (revert to draft).
    tx.set(masterRef, mergeIndexData(masterSnap.exists() ? masterSnap.data() : null, run.runId, {
      runId: run.runId,
      periodStart: toYMDString(run.periodStart),
      periodEnd: toYMDString(run.periodEnd),
      status: 'draft',
      employeeIds,
    }));

    // Caller-safe return: replace serverTimestamp sentinel with the JS Date
    // we already used in audit/lastRejection so callers can .toDate() safely.
    return {
      ...current,
      ...updates,
      lastModified: nowJs,
    };
  });

  // DTR locks are derived from the index (C1): the in-tx index write above reverted
  // this run to 'draft', so there's no per-employee lock release. lockResult is kept
  // as a success no-op for the unchanged return shape.
  const lockResult = { success: true, errors: [] };

  // Summary counter decrement — non-fatal.
  try {
    await incrementPendingApproval(companyId, -1);
  } catch (summaryErr) {
    console.error('[payrollCompletionService] Failed to decrement pending counter:', summaryErr);
  }

  return { updatedRun, lockResult };
};
